import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import type { Ref } from 'vue';
import { onUnmounted, ref } from 'vue';
import type { IOdds } from '@/interface/IOdds';
import type { LeagueSize, SportType } from '@/interface/enum';
import { PriceStyle } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { useOddsToken } from '@/libraries/oddsApi/composables/useOddsToken';
import { formatOdds } from '@/libraries/oddsApi/oddsApiFormatter';
import { EnumOddsCategoryType, EnumPresetFilterType, useOddsSubscription } from '@/types';

export function useOddsSubscriptionCallback({
    eventId,
    oddsId,
    isLive,
    isMixParlay,
    priceStyle,
    isActive = ref(true),
    sportType,
    leagueSize,
}: {
    eventId: number;
    oddsId?: number;
    isLive: boolean;
    isMixParlay?: boolean;
    priceStyle: MaybeRef<PriceStyle>;
    isActive?: Ref<boolean>;
    sportType: SportType;
    leagueSize: LeagueSize;
}) {
    const { getOddsToken } = useOddsToken();

    const variables = deepCompareComputed(() => {
        const oddsToken = getOddsToken(get(sportType), get(leagueSize));

        return ({
            query: {
                id: eventId,
                oddsId,
                filter: isLive ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive,
                oddsCategory: isMixParlay ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
                priceStyle: PriceStyle.toOddsApi(get(priceStyle)),
                oddsToken,
            },
        });
    });

    const { onSubscription } = useOddsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive) && !!variables.value.query.oddsToken,
        })),
    );

    type UpdatedFn = (odds: IOdds) => void;
    let updatedFn: UpdatedFn[] = [];
    const onUpdated = (callback: UpdatedFn) => {
        updatedFn.push(callback);
        onUnmounted(() => {
            updatedFn = updatedFn.filter(fn => fn !== callback);
        });
    };

    type DeletedFn = (oddsId: number) => void;
    let deletedFn: DeletedFn[] = [];
    const onDeleted = (callback: DeletedFn) => {
        deletedFn.push(callback);
        onUnmounted(() => {
            deletedFn = deletedFn.filter(fn => fn !== callback);
        });
    };

    onSubscription((subscription) => {
        subscription?.updated.forEach((toUpdate) => {
            if (toUpdate.id === oddsId) {
                const updatedOdds = Object.freeze(formatOdds(toUpdate));
                updatedFn.forEach(fn => fn(updatedOdds));
            }
        });

        subscription?.deleted.forEach((toDeleteId) => {
            if (toDeleteId === oddsId) {
                deletedFn.forEach(fn => fn(toDeleteId));
            }
        });
    });

    return {
        onUpdated,
        onDeleted,
    };
}
